// The Edit drawer of a secret: its settings first, then, from there, the replace-value form
// (with a back arrow), as the add drawer goes from the template picker to its form. Errors
// from the API stay in the drawer, above the form that caused them.
import { Warning } from "@phosphor-icons/react";
import { useState } from "react";

import { useT } from "../i18n";
import { EditSecretForm } from "./EditSecretForm";
import { ReplaceValueForm } from "./ReplaceValueForm";
import type { Signer } from "./requests";
import { DrawerHeader, SecretDrawer } from "./SecretDrawer";
import type { Credential } from "./templates";

interface Props {
  signer: Signer;
  signerName: string;
  /** The secret being edited; the drawer is closed without one. */
  credential: Credential | undefined;
  onUpdated: (credential: Credential) => void;
  onReplaced: (credential: Credential) => void;
  onClose: () => void;
}

export function EditSecretDrawer({ credential, onClose, ...rest }: Props) {
  return (
    <SecretDrawer open={!!credential} onClose={onClose}>
      {credential ? <Editing key={credential.id} credential={credential} onClose={onClose} {...rest} /> : null}
    </SecretDrawer>
  );
}

function Editing({ signer, signerName, credential, onUpdated, onReplaced, onClose }: Props & { credential: Credential }) {
  const t = useT().secrets.forms;
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState<string>();
  const switchTo = (next: boolean) => {
    setError(undefined);
    setReplacing(next);
  };
  return (
    <>
      <DrawerHeader
        title={replacing ? t.replace.title(credential.name) : t.edit.title(credential.name)}
        onBack={replacing ? () => switchTo(false) : undefined}
        onClose={onClose}
      />
      {error ? (
        <p role="alert" className="mx-5 mb-0 mt-4 flex gap-2 rounded-lg border border-red/30 bg-red-lighter/50 px-3 py-2 text-[13px] text-red-dark">
          <Warning weight="fill" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
      {replacing ? (
        <ReplaceValueForm
          signer={signer}
          signerName={signerName}
          credential={credential}
          onSaved={onReplaced}
          onError={setError}
          onCancel={() => switchTo(false)}
        />
      ) : (
        <EditSecretForm
          signer={signer}
          signerName={signerName}
          credential={credential}
          onSaved={onUpdated}
          onError={setError}
          onReplace={() => switchTo(true)}
          onCancel={onClose}
        />
      )}
    </>
  );
}
